import ServiceState from '../components/ServiceState';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscriptionPlans } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import type { SubscriptionPlan } from '../types';

function PricingPage() {
  const { isAuthenticated } = useAuth();
  const { data, isLoading, error, refetch } = useSubscriptionPlans();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const returnedPlans = (data as { plans?: unknown })?.plans;
  const plans: SubscriptionPlan[] = Array.isArray(returnedPlans) ? returnedPlans.filter((plan): plan is SubscriptionPlan => Boolean(plan && typeof plan === 'object' && typeof plan.id === 'string' && typeof plan.name === 'string')) : [];

  const displayPlans = plans.filter(plan => Number.isFinite(plan.priceMonthly) && plan.priceMonthly >= 0 && (plan.priceYearly === null || (Number.isFinite(plan.priceYearly) && plan.priceYearly >= 0)));

  const startCheckout = async (plan: SubscriptionPlan) => {
    setCheckoutError(null);

    if (!isAuthenticated) {
      navigate(`/register?plan=${encodeURIComponent(plan.id)}&billingCycle=${billingCycle}`);
      return;
    }

    setCheckoutPlanId(plan.id);
    const response = await api.createSubscription(plan.id, billingCycle);
    if (response.success && response.data) {
      if (response.data.checkoutUrl) {
        window.location.assign(response.data.checkoutUrl);
      } else {
        navigate('/dashboard?subscription=active');
      }
    } else {
      setCheckoutError(response.error || 'Unable to start checkout. Please try again.');
    }
    setCheckoutPlanId(null);
  };

  return (
    <div>
      <div className="pricing-hero">
        <div className="section-label">Scope before commitment</div>
        <h1>Available service plans</h1>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '1rem auto' }}>
          Review the plans returned by the marketplace service and choose a billing term before continuing.
        </p>
        <div className="billing-toggle" role="group" aria-label="Billing cycle">
          <button className={billingCycle === 'monthly' ? 'active' : ''} aria-pressed={billingCycle === 'monthly'} onClick={() => setBillingCycle('monthly')}>Monthly</button>
          <button className={billingCycle === 'yearly' ? 'active' : ''} aria-pressed={billingCycle === 'yearly'} onClick={() => setBillingCycle('yearly')}>Yearly <span>Annual term</span></button>
        </div>
        {searchParams.get('checkout') === 'success' && (
          <div className="checkout-message checkout-success">Checkout returned. Your access will appear after the payment is verified.</div>
        )}
        {searchParams.get('checkout') === 'cancelled' && (
          <div className="checkout-message">Checkout was cancelled. Your account is unchanged.</div>
        )}
        {checkoutError && <div className="checkout-message checkout-error" role="alert">{checkoutError}</div>}
      </div>

      {isLoading ? (
        <div className="loading">Loading plans...</div>
      ) : error || !displayPlans.length ? <ServiceState error={error} retry={refetch} empty="No service plans published yet" /> : (
        <div className="pricing-grid">
          {displayPlans.map((plan, index) => (
            <div 
              key={plan.id} 
              className={`card pricing-card ${index === 1 ? 'featured' : ''}`}
            >
              
              <h2 className="pricing-name">{plan.name}</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>{typeof plan.description === 'string' ? plan.description : ''}</p>
              
              <div className="pricing-price">
                ${billingCycle === 'yearly' && plan.priceYearly !== null ? plan.priceYearly : plan.priceMonthly}
                <span>/{billingCycle === 'yearly' && plan.priceYearly !== null ? 'year' : 'month'}</span>
              </div>
              
              {plan.priceYearly && billingCycle === 'monthly' && (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  ${plan.priceYearly}/year (save ${plan.priceMonthly * 12 - plan.priceYearly})
                </p>
              )}

              <ul className="pricing-features">
                {(Array.isArray(plan.features) ? plan.features.filter(feature => typeof feature === 'string') : []).map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>

              <button
                onClick={() => startCheckout(plan)}
                disabled={checkoutPlanId !== null || (billingCycle === 'yearly' && plan.priceYearly === null)}
                className={`btn ${index === 1 ? 'btn-primary' : 'btn-secondary'} btn-lg`}
                style={{ width: '100%' }}
              >
                {checkoutPlanId === plan.id ? 'Opening checkout...' : (billingCycle === 'yearly' && plan.priceYearly === null ? 'Yearly plan unavailable' : plan.priceMonthly === 0 ? 'Get Started' : 'Start plan')}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="hero-note">Review the selected plan’s terms. A return URL alone does not confirm payment or access.</p>
    </div>
  );
}

export default PricingPage;
